import React, { useState, useEffect } from "react";
import { Collapse, Paper, Typography, Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlanSelectionModal from "./PlanSelectionModal";

const FloatingContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: "20px",
  left: "10px", // Más a la izquierda
  zIndex: 9998,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  [theme.breakpoints.down("sm")]: {
    left: "10px",
    bottom: "15px",
  },
}));

const TrialPaper = styled(Paper)(({ theme }) => ({
  padding: "12px",
  backgroundColor: "#252526",
  color: "white",
  minWidth: "240px",
  maxWidth: "280px",
  borderRadius: "10px",
  boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
  border: "1px solid #404040",
  [theme.breakpoints.down("sm")]: {
    minWidth: "200px",
    maxWidth: "240px",
    padding: "10px",
  },
}));

const TrialButton = styled(Box)(({ theme }) => ({
  backgroundColor: "#252526",
  color: "white",
  padding: "10px 12px",
  borderRadius: "8px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minWidth: "160px",
  boxShadow: "0 3px 12px rgba(0,0,0,0.2)",
  border: "1px solid #404040",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#333333",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: "auto",
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    padding: "0",
    justifyContent: "center",
    "& > div": {
      display: "none", // Ocultar el texto en móvil
    },
  },
}));

const TrialFloatingButton = ({ userSubscription }) => {
  const [expanded, setExpanded] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (userSubscription && userSubscription.subscription) {
      const { status, next_payment_date } = userSubscription.subscription;

      // Solo mostrar si está en prueba gratuita
      if (status === "FREE_TRIAL" && next_payment_date) {
        setIsTrialActive(true);

        // Calcular días restantes
        const currentDate = new Date();
        const endDate = new Date(next_payment_date);
        const timeDiff = endDate.getTime() - currentDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

        setDaysRemaining(Math.max(0, daysDiff));
      } else {
        setIsTrialActive(false);
      }
    }
  }, [userSubscription]);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const handleOpenPlanModal = () => {
    setShowPlanModal(true);
    setExpanded(false); // Cerrar el panel expandido
  };

  const handleClosePlanModal = () => {
    setShowPlanModal(false);
  };

  const handleSelectPlan = (_planKey) => {
    // Aquí puedes agregar la lógica para procesar la selección del plan
    // Podrías enviar esto al backend, redirigir a una página de pago, etc.
  };

  // Solo renderizar si hay una prueba activa
  if (!isTrialActive) {
    return null;
  }

  const getTrialMessage = () => {
    if (daysRemaining <= 0) {
      return "Tu prueba gratuita ha expirado";
    } else if (daysRemaining === 1) {
      return "Tu período de prueba estará vigente por menos de un día";
    } else {
      return `Tu período de prueba estará vigente por ${daysRemaining} días`;
    }
  };

  const getShortTrialMessage = () => {
    if (daysRemaining <= 0) {
      return "Prueba expirada";
    } else if (daysRemaining === 1) {
      return "Menos de un día";
    } else {
      return `${daysRemaining} días restantes`;
    }
  };

  const getTrialEndDate = () => {
    if (userSubscription?.subscription?.next_payment_date) {
      const endDate = new Date(userSubscription.subscription.next_payment_date);
      return endDate.toLocaleDateString("es-ES", {
        day: "numeric",
        month: "long",
      });
    }
    return "";
  };

  return (
    <FloatingContainer>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <TrialPaper elevation={3}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: "15px", fontWeight: "bold" }}>
            {getTrialMessage()}
          </Typography>
          {daysRemaining > 0 && (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: "#cccccc", fontSize: "13px" }}>
                Tu prueba termina el {getTrialEndDate()}
              </Typography>
              {/* <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                Selecciona un plan y obtén:
              </Typography>
              <Box component="ul" sx={{ margin: 0, paddingLeft: "20px", color: "#cccccc" }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Primeros 3 meses por 1 $/mes
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  20 $ USD de descuento en dominio
                </Typography>
              </Box> */}
              <Box
                sx={{
                  backgroundColor: "#FF770D",
                  color: "white",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  textAlign: "center",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: "13px",
                  marginTop: "10px",
                  "&:hover": {
                    backgroundColor: "#e5660a",
                  },
                }}
                onClick={handleOpenPlanModal}
              >
                Seleccionar plan
              </Box>
            </>
          )}
          {daysRemaining <= 0 && (
            <Box
              sx={{
                backgroundColor: "#FF770D",
                color: "white",
                padding: "8px 12px",
                borderRadius: "6px",
                textAlign: "center",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "13px",
                marginTop: "10px",
                "&:hover": {
                  backgroundColor: "#e5660a",
                },
              }}
              onClick={handleOpenPlanModal}
            >
              Activar Plan
            </Box>
          )}
        </TrialPaper>
      </Collapse>

      <TrialButton onClick={handleToggle}>
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Typography variant="body2" sx={{ fontSize: "13px", fontWeight: "600", color: "white" }}>
            Prueba gratuita
          </Typography>
          <Typography variant="caption" sx={{ fontSize: "11px", color: "#cccccc" }}>
            {getShortTrialMessage()}
          </Typography>
        </Box>
        {isMobile ? (
          <AccessTimeIcon sx={{ color: "white", fontSize: "24px" }} />
        ) : expanded ? (
          <ExpandLessIcon />
        ) : (
          <ExpandMoreIcon />
        )}
      </TrialButton>

      {/* Modal de selección de planes */}
      <PlanSelectionModal open={showPlanModal} onClose={handleClosePlanModal} onSelectPlan={handleSelectPlan} />
    </FloatingContainer>
  );
};

export default TrialFloatingButton;
